import React from 'react';
import _ from 'lodash';
import { Stepper, Step, StepLabel, Tab, Tabs } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import PaymentIcon from '@mui/icons-material/MonetizationOn';
import DeliveryIcon from '@mui/icons-material/LocalShipping';
import { Button, Input, Message, Navigate, Select } from '../components';
import { formatAmount, formatDate, isEmptyString, request, toNumber } from '../functions';
import { COMPLAINT_TYPES, IMAGES } from '../config';
import at from '../translations/public-order-at.json';
import be from '../translations/public-order-be.json';
import bg from '../translations/public-order-bg.json';
import cy from '../translations/public-order-cy.json';
import cz from '../translations/public-order-cz.json';
import de from '../translations/public-order-de.json';
import dk from '../translations/public-order-dk.json';
import ee from '../translations/public-order-ee.json';
import en from '../translations/public-order-en.json';
import es from '../translations/public-order-es.json';
import fi from '../translations/public-order-fi.json';
import fr from '../translations/public-order-fr.json';
import gr from '../translations/public-order-gr.json';
import hr from '../translations/public-order-hr.json';
import hu from '../translations/public-order-hu.json';
import ie from '../translations/public-order-ie.json';
import it from '../translations/public-order-it.json';
import lt from '../translations/public-order-lt.json';
import lu from '../translations/public-order-lu.json';
import lv from '../translations/public-order-lv.json';
import mt from '../translations/public-order-mt.json';
import nl from '../translations/public-order-nl.json';
import pl from '../translations/public-order-pl.json';
import pt from '../translations/public-order-pt.json';
import ro from '../translations/public-order-ro.json';
import se from '../translations/public-order-se.json';
import si from '../translations/public-order-si.json';
import sk from '../translations/public-order-sk.json';
import '../assets/styles/order-page.css';

/**
 * OrderPage komponenta.
 */
class OrderPage extends Navigate {
    state = {
        translate: null,
        tab: 'delivery',
        loadingCancel: false,
        loadingComplaint: false,
        lightbox: {
            cancel: false,
            complaint: false,
            complaintComment: false,
        },
    };

    translations = { at, be, bg, cy, cz, de, dk, ee, en, es, fi, fr, gr, hr, hu, ie, it, lt, lu, lv, mt, nl, pl, pt, ro, se, si, sk };

    getTranslate() {
        const { translate } = this.state;
        const { data } = this.props;

        if (translate === null) {
            // Default podla dodania objednavky
            const translations = this.getTranslations();
            const deliveryCountry = data.order.client.delivery_country.toLowerCase();

            return _.has(translations, deliveryCountry) ? deliveryCountry : 'en';
        }

        return translate;
    }

    getTranslations() {
        return this.translations;
    }

    /**
     * Preklad.
     *
     * @param {string} text
     *
     * @return {string}
     */
    translate(text) {
        const translate = this.getTranslate();
        const translations = this.getTranslations();

        if (!_.has(translations, translate)) {
            return text;
        }

        const translated = _.has(translations[translate], text) ? translations[translate][text] : text;

        if (translated !== '') {
            return translated;
        }

        return text;
    }

    onChangeComplaint(name, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, complaint: { ...lightbox.complaint, [name]: value } } });
    }

    onChangeComplaintComment(name, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, complaintComment: { ...lightbox.complaintComment, [name]: value } } });
    }

    onChangeComplaintProduct(id, value, max) {
        const { lightbox } = this.state;

        this.onChangeComplaint('products', { ...lightbox.complaint.products, [id]: Math.max(Math.min(toNumber(value), max), 0) });
    }

    downloadPdf() {
        const { data } = this.props;

        request(`/track-trace/pdf/${data.id}/${data.token}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', this.translate('Nepodarilo sa stiahnuť faktúru'));
                return;
            }

            if (data.link.indexOf('inline') !== -1) {
                // Chceme inline
                window.open(data.link, '_blank');
                return;
            }

            // Stiahneme subor
            window.location = data.link;
        });
    }

    cancel() {
        const { data } = this.props;

        this.closeLightbox('cancel');

        this.setState({ loadingCancel: true });

        request(`/track-trace/cancel/${data.id}/${data.token}`).then(response => {
            const { status } = response.data;

            this.setState({ loadingCancel: false });

            if (status === 'error') {
                this.showSnackbar('error', this.translate('Nepodarilo sa stornovať objednávku'));
                return;
            }

            this.showSnackbar('success', this.translate('Objednávka bola stornovaná'));

            setTimeout(() => window.location.reload(), 200);
        });
    }

    saveComplaint() {
        const { data } = this.props;
        const { lightbox } = this.state;

        // Celkovky pocet ks
        const totalQuantity = _.reduce(lightbox.complaint.products, (result, quantity) => {
            return result + toNumber(quantity);
        }, 0);

        let error = null;

        if (isEmptyString(lightbox.complaint.text)) {
            // Nie je vyplneny popis
            error = this.translate('Prosím vyplňte popis reklamácie');
        } else if (totalQuantity === 0) {
            error = this.translate('Prosím zadajte aspoň jeden produkt');
        }

        if (error !== null) {
            this.showSnackbar('error', error);
            return;
        }

        this.setState({ loadingComplaint: true });

        request(`/track-trace/complaint/${data.id}/${data.token}`, lightbox.complaint, 'POST').then(response => {
            const { status } = response.data;

            this.setState({ loadingComplaint: false });

            if (status === 'error') {
                this.showSnackbar('error', this.translate('Nepodarilo sa reklamovať objednávku'));
                return;
            }

            this.showSnackbar('success', this.translate('Objednávka bola reklamovaná'));

            setTimeout(() => window.location.reload(), 200);
        });
    }

    saveComplaintComment() {
        const { data } = this.props;
        const { lightbox } = this.state;

        let error = null;

        if (isEmptyString(lightbox.complaintComment.text)) {
            // Nie je vyplneny popis
            error = this.translate('Prosím vyplňte vyjadrenie k reklamácii');
        }

        if (error !== null) {
            this.showSnackbar('error', error);
            return;
        }

        this.setState({ loadingComplaint: true });

        request(`/track-trace/complaint-comment/${data.id}/${data.token}`, lightbox.complaintComment, 'POST').then(response => {
            const { status } = response.data;

            this.setState({ loadingComplaint: false });

            if (status === 'error') {
                this.showSnackbar('error', this.translate('Nepodarilo sa uložiť vyjadrenie'));
                return;
            }

            this.showSnackbar('success', this.translate('Vyjadrenie bolo uložené'));

            setTimeout(() => window.location.reload(), 200);
        });
    }

    render() {
        const { data } = this.props;
        const { tab, loadingCancel, loadingComplaint, lightbox } = this.state;

        const steps = {
            confirm: this.translate('Prijatá'),
            processing: this.translate('Spracováva sa'),
            send: this.translate('Odoslaná'),
            delivered: this.translate('Doručená'),
        };

        // Preklady
        const translateOptions = _.reduce(_.keys(this.getTranslations()), (result, country) => {
            return { ...result, [country]: country };
        }, {});

        // Aktualny preklad
        const translate = this.getTranslate();

        // Vytiahneme DPHcki
        const taxes = _.reduce(data.order.products, (result, product) => {
            return [ ...result, toNumber(product.tax) ];
        }, []);

        // Vytiahneme DPH
        const tax = taxes.length > 0 ? Math.max(...taxes) : 0;

        // Dodanie bez DPH
        const deliveryAmountWithoutVat = data.order.delivery_type_amount * (100 / (100 + tax));

        // Platba bez DPH
        const paymentAmountWithoutVat = data.order.payment_type_amount * (100 / (100 + tax));

        // Diff bez DPH
        const diffAmountWithoutVat = data.order.amount_diff * (100 / (100 + tax));

        // Celkova suma bez DPH
        let totalWithoutVat = deliveryAmountWithoutVat + paymentAmountWithoutVat + diffAmountWithoutVat;

        let step = 0;
        let text = '';

        _.each(data.settings.states, ({ states, texts }, state) => {
            if (states.indexOf('===') !== -1) {
                states = _.split(states, '===');
            } else {
                states = [states];
            }

            if (_.includes(states, data.order.state.id)) {
                step = toNumber(_.invert(_.keys(data.settings.states))[state]);
                text = _.has(texts, translate) && texts[translate] !== '' ? texts[translate] : '';
            }
        });

        // Je zrusena
        const isCanceled = data.order.state.id === data.settings.cancel_state;

        // Ma reklamaciu?
        const hasComplaint = !_.isEmpty(data.complaint);

        return (
            <div className="order-page">
                <div className="order-page__eshop">
                    <Select
                        options={translateOptions}
                        value={translate}
                        onChange={value => this.setState({ translate: value })}
                        allowEmpty={false}
                        countries
                    />
                    {data.logo !== '' ? <img src={`${data.logo}?v=${Math.random()}`} alt="eshop logo" /> : null}
                    {data.settings.contact !== '' ? <Button
                        onClick={() => window.open(`mailto:${data.settings.contact}`, '_blank')}
                        color="shadow"
                    >{this.translate('Opýtať sa na stav')}</Button> : null}
                </div>
                <div className="order-page__header">
                    <div className="order-page__header__title">{`${this.translate('Objednávka č.')} ${data.order.number}`}</div>
                    <Stepper activeStep={isCanceled ? 1 : step} className="order-page__header__states">
                        {_.map(isCanceled ? { confirm: this.translate('Prijatá'), canceled: this.translate('Stornovaná') } : steps, (step, key) => {
                            return (
                                <Step key={key}>
                                    <StepLabel>{step}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </div>
                <div className="order-page__text">{text}</div>
                <div className="order-page__info">
                    <div className="order-page__info__panel">
                        <div className={`order-page__info__panel__paid ${data.order.paid ? 'paid' : ''}`}>
                            <div className="order-page__info__panel__paid__icon">
                                <div className="order-page__info__panel__paid__icon__dot" />
                            </div>
                            <div className="order-page__info__panel__paid__name">{this.translate(data.order.paid ? 'Uhradené' : 'Neuhradené')}</div>
                        </div>
                        {!_.isEmpty(data.invoice) ? <div className="order-page__info__panel__invoice">
                            <img src={IMAGES['pdf.png']} alt="pdf" />
                            <div className="order-page__info__panel__invoice__name">{`${this.translate('Faktúra č.')} ${data.invoice.number}`}</div>
                            <a onClick={() => this.downloadPdf()}>{this.translate('Stiahnuť')}</a>
                        </div> : null}
                        {!_.isEmpty(data.pdf_link) ? <div className="order-page__info__panel__invoice">
                            <img src={IMAGES['pdf.png']} alt="pdf" />
                            <div className="order-page__info__panel__invoice__name">{this.translate('Faktúra')}</div>
                            <a onClick={() => window.open(data.pdf_link, '_blank')}>{this.translate('Stiahnuť')}</a>
                        </div> : null}
                        {!_.isEmpty(data.package.number) ? <div className="order-page__info__panel__package">
                            <img src={IMAGES[`${data.package.courier.toLowerCase()}-mini.png`]} alt={data.package.courier} />
                            <div className="order-page__info__panel__package__name">{`${this.translate('Zásielka č.')} ${data.package.number}`}</div>
                            <a href={data.package.url} target="_blank">{this.translate('Sledovať')}</a>
                        </div> : null}
                        {!_.isEmpty(data.package.number) && data.package_info !== '' ? <div className="order-page__info__panel__package-info">{data.package_info}</div> : null}
                        <div className="order-page__info__panel__buttons">
                            {data.settings.cancel && data.settings.cancel_state !== '' && step === 0 && !isCanceled && !hasComplaint ? <Button
                                onClick={() => this.showLightbox('cancel')}
                                color="red"
                                loading={loadingCancel}
                            >{this.translate('Stornovať')}</Button> : null}
                            {data.settings.complaints && !isCanceled && !hasComplaint ? <Button
                                onClick={() => this.showLightbox('complaint', {
                                    type: _.keys(COMPLAINT_TYPES)[0],
                                    text: '',
                                    products: _.reduce(data.order.products, (result, { id }) => ({ ...result, [id]: '' }), {}),
                                })}
                                color="shadow"
                            >{this.translate('Reklamovať')}</Button> : null}
                            {hasComplaint ? <div className="order-page__info__panel__complaint">
                                {data.complaint.status === 'waiting' ? <Message
                                    type="warning"
                                >{`${this.translate('Spracovávame reklamáciu č.')} ${data.complaint.number}`}</Message> : null}
                                {data.complaint.status === 'processed' ? <Message
                                    type="success"
                                >{this.translate('Reklamácia bola vyriešená')}</Message> : null}
                                <div className="order-page__info__panel__complaint__text">
                                    <div className="order-page__info__panel__complaint__text__date">{formatDate(data.complaint.status === 'waiting' ? data.complaint.date : data.complaint.end_date, 'dd.mm.yyyy')}</div>
                                    <div>{data.complaint.status === 'waiting' ? data.complaint.text : data.complaint.end_text}</div>
                                </div>
                                {data.complaint.end_comment !== '' ? <div className="order-page__info__panel__complaint__text">
                                    <div className="order-page__info__panel__complaint__text__date">{formatDate(data.complaint.end_comment_date, 'dd.mm.yyyy')}</div>
                                    <div>{data.complaint.end_comment}</div>
                                </div> : null}
                                {data.complaint.status === 'processed' && data.complaint.end_comment === '' ? <Button
                                    onClick={() => this.showLightbox('complaintComment', { text: '' })}
                                    color="shadow"
                                >{this.translate('Pridať vyjadrenie')}</Button> : null}
                            </div> : null}
                        </div>
                    </div>
                    <div className="order-page__info__client">
                        <Tabs
                            value={tab}
                            onChange={(event, tab) => this.setState({ tab })}
                            className="order-page__info__client__tabs"
                        >
                            <Tab label={this.translate('Fakturačná adresa')} value="billing" />
                            <Tab label={this.translate('Dodacia adresa')} value="delivery" />
                        </Tabs>
                        {tab === 'billing' ? <div className="order-page__info__client__panel">
                            <div className="order-page__info__client__panel__line">{`${data.order.client.name}${data.order.client.contact_name !== '' ? ' / ' + data.order.client.contact_name : ''}`}</div>
                            {data.order.client.ico !== '' ? <div className="order-page__info__client__panel__line">{`${this.translate('IČO')}: ${data.order.client.ico}, ${this.translate('DIČ')}: ${data.order.client.dic}${data.order.client.ic_dph !== '' ? (', ' + this.translate('IČ DPH') + ': ' + data.order.client.ic_dph) : ''}`}</div> : null}
                            <div className="order-page__info__client__panel__line">
                                <a href={`tel:${data.order.client.phone}`}>{data.order.client.phone}</a>
                                <a href={`mailto:${data.order.client.email}`}>{data.order.client.email}</a>
                            </div>
                            <div className="order-page__info__client__panel__line">{`${data.order.client.address} ${data.order.client.city} ${data.order.client.zip}`}</div>
                            <div className="order-page__info__client__panel__line">{_.has(data.countries, data.order.client.country.toLowerCase()) ? data.countries[data.order.client.country.toLowerCase()] : data.order.client.country}</div>
                        </div> : <div className="order-page__info__client__panel">
                            <div className="order-page__info__client__panel__line">{`${data.order.client.delivery_name}${data.order.client.delivery_contact_name !== '' ? ' / ' + data.order.client.delivery_contact_name : ''}`}</div>
                            <div className="order-page__info__client__panel__line">
                                <a href={`tel:${data.order.client.delivery_phone}`}>{data.order.client.delivery_phone}</a>
                            </div>
                            <div className="order-page__info__client__panel__line">{`${data.order.client.delivery_address} ${data.order.client.delivery_city} ${data.order.client.delivery_zip}`}</div>
                            <div className="order-page__info__client__panel__line">{_.has(data.countries, data.order.client.delivery_country.toLowerCase()) ? data.countries[data.order.client.delivery_country.toLowerCase()] : data.order.client.delivery_country}</div>
                        </div>}
                    </div>
                </div>
                <div className="order-page__products">
                    <div className="order-page__products__title">{this.translate('Tovar v objednávke')}</div>
                    {_.map(data.order.products, (product, key) => {
                        let productData = {};

                        if (_.has(data.products, product.id)) {
                            productData = {
                                img: data.products[product.id].img_url,
                                name: `${data.products[product.id].name}${!_.isEmpty(product.variant) ? ` - ${product.variant}` : ''}`,
                                url: data.products[product.id].url,
                            };
                        } else {
                            productData = {
                                img: '',
                                name: product.name,
                                url: product.url,
                            };
                        }

                        totalWithoutVat += product.unit_price * product.quantity;

                        return (
                            <div className="order-page__products__product" key={key}>
                                <div className="order-page__products__product__left">
                                    <div className="order-page__products__product__left__photo">
                                        {!_.isEmpty(productData.img) ? <img src={productData.img} alt={productData.name} /> : <HelpIcon />}
                                    </div>
                                    <div className="order-page__products__product__left__info">
                                        <a href={productData.url} className="order-page__products__product__left__info__name" target="_blank">{_.truncate(productData.name, { length: 64 })}</a>
                                        <div className="order-page__products__product__left__info__values">
                                            <div>{this.translate('Číslo produktu')}: {product.number !== '' ? product.number : '-'}</div>
                                            <div>{this.translate('EAN')}: {product.ean !== '' ? product.ean : '-'}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="order-page__products__product__status">{this.translate('Rezervované')}</div>
                                <div className="order-page__products__product__quantity">{product.quantity}{`${!_.isEmpty(product.unit) ? ` ${product.unit}` : 'x'}`}</div>
                                <div className="order-page__products__product__price">
                                    <span>{formatAmount(product.total_price * product.quantity, data.order.currency)}</span>
                                    <span>{`${formatAmount(product.unit_price * product.quantity, data.order.currency)} ${this.translate('bez DPH')}`}</span>
                                </div>
                            </div>
                        );
                    })}
                    <div className="order-page__products__product">
                        <div className="order-page__products__product__left">
                            <div className="order-page__products__product__left__photo">
                                <DeliveryIcon />
                            </div>
                            <div className="order-page__products__product__left__info">
                                <div className="order-page__products__product__left__info__name">{_.truncate(data.order.delivery_type_name, { length: 96 })}</div>
                            </div>
                        </div>
                        <div className="order-page__products__product__price">
                            <span>{formatAmount(data.order.delivery_type_amount, data.order.currency)}</span>
                            <span>{`${formatAmount(deliveryAmountWithoutVat, data.order.currency)} ${this.translate('bez DPH')}`}</span>
                        </div>
                    </div>
                    <div className="order-page__products__product">
                        <div className="order-page__products__product__left">
                            <div className="order-page__products__product__left__photo">
                                <PaymentIcon />
                            </div>
                            <div className="order-page__products__product__left__info">
                                <div className="order-page__products__product__left__info__name">{_.truncate(data.order.payment_type_name, { length: 96 })}</div>
                            </div>
                        </div>
                        <div className="order-page__products__product__price">
                            <span>{formatAmount(data.order.payment_type_amount, data.order.currency)}</span>
                            <span>{`${formatAmount(paymentAmountWithoutVat, data.order.currency)} ${this.translate('bez DPH')}`}</span>
                        </div>
                    </div>
                    {data.order.amount_diff !== 0 ? <div className="order-page__products__product">
                        <div className="order-page__products__product__left">
                            <div className="order-page__products__product__left__photo">
                                <PaymentIcon />
                            </div>
                            <div className="order-page__products__product__left__info">
                                <div className="order-page__products__product__left__info__name">{this.translate('Zľava')}</div>
                            </div>
                        </div>
                        <div className="order-page__products__product__price">
                            <span>{formatAmount(data.order.amount_diff, data.order.currency)}</span>
                            <span>{`${formatAmount(diffAmountWithoutVat, data.order.currency)} ${this.translate('bez DPH')}`}</span>
                        </div>
                    </div> : null}
                </div>
                <div className="order-page__down">
                    <div className="order-page__down__left">{this.translate('Vytvorené')} {formatDate(data.order.date, 'dd.mm.yyyy')}</div>
                    <div className="order-page__down__summary">
                        <div className="order-page__down__summary__line">
                            <div className="order-page__down__summary__line__name">{this.translate('Celkom')}</div>
                            <div className="order-page__down__summary__line__value">{formatAmount(data.order.amount, data.order.currency)}</div>
                        </div>
                        <div className="order-page__down__summary__line">
                            <div className="order-page__down__summary__line__name">{this.translate('bez DPH')}</div>
                            <div className="order-page__down__summary__line__value">{formatAmount(totalWithoutVat, data.order.currency)}</div>
                        </div>
                    </div>
                </div>
                {this.renderLightbox(
                    'complaint',
                    this.translate('Reklamácia'),
                    !_.isEmpty(lightbox.complaint) ? <div className="complaint-lightbox">
                        <Select
                            label={this.translate('Žiadosť zákaznika na vybavenie')}
                            value={lightbox.complaint.type}
                            options={COMPLAINT_TYPES}
                            onChange={value => this.onChangeComplaint('type', value)}
                            allowEmpty={false}
                        />
                        <Input
                            label={this.translate('Popis')}
                            value={lightbox.complaint.text}
                            onChange={value => this.onChangeComplaint('text', value)}
                            multiline
                        />
                        <Message type="info">{this.translate('Nižšie prosím vyberte konkrétne produkty a počty kusov')}</Message>
                        {_.map(data.order.products, (product, key) => {
                            let productData = {};

                            if (_.has(data.products, product.id)) {
                                productData = {
                                    img: data.products[product.id].img_url,
                                    name: `${data.products[product.id].name}${!_.isEmpty(product.variant) ? ` - ${product.variant}` : ''}`,
                                    url: data.products[product.id].url,
                                };
                            } else {
                                productData = {
                                    img: '',
                                    name: product.name,
                                    url: product.url,
                                };
                            }

                            return (
                                <div className="complaint-lightbox__product" key={key}>
                                    <div className="complaint-lightbox__product__left">
                                        <div className="complaint-lightbox__product__left__photo">
                                            {!_.isEmpty(productData.img) ? <img src={productData.img} alt={productData.name} /> : <HelpIcon />}
                                        </div>
                                        <div className="complaint-lightbox__product__left__info">
                                            <a href={productData.url} className="complaint-lightbox__product__left__info__name" target="_blank">{productData.name}</a>
                                            <div className="complaint-lightbox__product__left__info__values">
                                                <div>{this.translate('Číslo produktu')}: {product.number !== '' ? product.number : '-'}</div>
                                                <div>{this.translate('EAN')}: {product.ean !== '' ? product.ean : '-'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <Input
                                        placeholder={product.quantity.toString()}
                                        value={lightbox.complaint.products[product.id]}
                                        onChange={value => this.onChangeComplaintProduct(product.id, value, toNumber(product.quantity))}
                                        type="number"
                                    />
                                </div>
                            );
                        })}
                        <Button
                            onClick={() => this.saveComplaint()}
                            loading={loadingComplaint}
                            className="complaint-lightbox__button"
                            color="green"
                        >{this.translate('Odoslať')}</Button>
                    </div> : null,
                )}
                {this.renderLightbox(
                    'complaintComment',
                    this.translate('Vyjadrenie k reklamácii'),
                    !_.isEmpty(lightbox.complaintComment) ? <div className="complaint-lightbox">
                        <Input
                            label={this.translate('Vyjadrenie')}
                            value={lightbox.complaintComment.text}
                            onChange={value => this.onChangeComplaintComment('text', value)}
                            multiline
                        />
                        <Button
                            onClick={() => this.saveComplaintComment()}
                            loading={loadingComplaint}
                            className="complaint-lightbox__button"
                            color="green"
                        >{this.translate('Odoslať')}</Button>
                    </div> : null
                )}
                {this.renderLightbox(
                    'cancel',
                    this.translate('Naozaj chcete stornovať túto objednávku?'),
                    null,
                    this.translate('Áno'),
                    this.translate('Nie'),
                    () => this.cancel()
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { OrderPage };
